import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, XCircle } from "lucide-react";
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const SHItemsTable = ({
  indentID,
  userId,
}) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [remarks, setRemarks] = useState("");
  const navigate = useNavigate();

  const fetchIndentItems = async () => {
    if (!indentID) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/shpage/indentitems`,
        {
          params: { indentId: indentID },
        }
      );

      const fetchedItems = response.data
        .filter((item) => item.IndentID === indentID)
        .map((item) => ({
          indentItemID: item.IndentItemID,
          itemName: item.ItemName,
          quantity: item.Quantity,
          quantityReceived: item.QuantityReceived,
          difference: Number(item.Quantity) - Number(item.QuantityReceived),
          stock: item.Stock || 0,
          categoryID: item.CategoryID,
          itemID: item.ItemID,
          remarks: item.Remarks,
          promisedDate: item.PromisedDate,
          commitedDate: item.CommitedDate,
          accountingUnitID: item.AccountingUnitID,
          unitname: item.UnitName,
          isForceClosed: Boolean(item.ForceClosure),
        }));

      setItems(fetchedItems);
    } catch (error) {
      console.error("Error fetching indent items:", error);
      setError("Failed to load indent items");
    }
  };

  useEffect(() => {
    fetchIndentItems();
  }, [indentID]);

  const handleItemForceClose = async () => {
    if (!selectedItemId || !remarks.trim()) return;
    
    setLoading(prev => ({ ...prev, [selectedItemId]: true }));
    
    try {
      await axios.post(`${API_BASE_URL}/shpage/indentitems/forceClose`, {
        indentItemId: selectedItemId,
        userId: userId,
        remarks: remarks.trim()
      });
      
      setItems(prevItems => 
        prevItems.map(item => 
          item.indentItemID === selectedItemId 
            ? { ...item, isForceClosed: true, remarks: remarks }
            : item
        )
      );

      await fetchIndentItems();
      setShowDialog(false);
      setSelectedItemId(null);
      setRemarks("");
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         "Failed to force close item";
      setError(errorMessage);
      console.error("Force Item Closure Error:", error.response?.data);
    } finally {
      setLoading(prev => ({ ...prev, [selectedItemId]: false }));
    }
  };

  const handleForceCloseIndent = async () => {
    try {
      await axios.post(`${API_BASE_URL}/shpage/indents/forceClose`, {
        indentId: indentID,
        userId: userId
      });
  
      navigate('/SHPage');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         "Failed to force close indent";
      setError(errorMessage);
      console.error("Force Indent Closure Error:", error.response?.data);
    }
  };

  const canForceCloseIndent = () => {
    const itemsWithDifference = items.filter(item => item.difference !== 0);
    const allForceClosed = itemsWithDifference.length > 0 && 
                          itemsWithDifference.every(item => item.isForceClosed);
    return allForceClosed;
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
          <button 
            onClick={() => setError(null)} 
            className="float-right font-bold"
          >
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {canForceCloseIndent() && (
        <div className="mb-4 flex justify-end">
          <button
            onClick={handleForceCloseIndent}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded flex items-center transition-colors duration-200"
          >
            <X className="mr-2" size={16} />
            Force Close Indent
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-gray-600">
          <thead className="bg-gray-100 text-gray-700 uppercase">
            <tr>
              <th className="px-4 py-3 text-left w-12">S.No</th>
              <th className="px-4 py-3 text-left">Item Name</th>
              <th className="px-4 py-3 text-left">AU</th>
              <th className="px-4 py-3 text-center w-24">Quantity</th>
              <th className="px-4 py-3 text-center w-24">Quantity Received</th>
              <th className="px-4 py-3 text-center w-24">Difference</th>
              <th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3 text-right">Commited Date</th>
              <th className="px-4 py-3 text-left">Remarks</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr
                key={index}
                className="border-b hover:bg-gray-50 transition-colors duration-200"
              >
                <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                <td className="px-4 py-3 font-medium">{item.itemName}</td>
                <td className="px-4 py-3 font-medium">{item.unitname}</td>
                <td className="px-4 py-3 text-center">{item.quantity}</td>
                <td className="px-4 py-3 text-center">{item.quantityReceived}</td>
                <td className="px-4 py-3 text-center">{item.difference.toFixed(3)}</td>
                <td className="px-4 py-3 text-right">{item.stock}</td>
                <td className="px-4 py-3 text-right">
                  {item.commitedDate
                    ? new Date(item.commitedDate).toLocaleDateString()
                    : "-"}
                </td>
                <td className="px-4 py-3 text-left">{item.remarks || "-"}</td>
                <td className="px-4 py-3 text-center">
                  {item.difference !== 0 && !item.isForceClosed ? (
                    <button
                      onClick={() => {
                        setSelectedItemId(item.indentItemID);
                        setShowDialog(true);
                      }}
                      disabled={loading[item.indentItemID]}
                      className={`
                        ${loading[item.indentItemID] 
                          ? 'bg-yellow-400 cursor-wait' 
                          : 'bg-yellow-500 hover:bg-yellow-600'
                        } 
                        text-white px-3 py-1 rounded text-sm transition-colors duration-200
                      `}
                    >
                      {loading[item.indentItemID] ? 'Processing...' : 'Force Close Item'}
                    </button>
                  ) : item.isForceClosed ? (
                    <span className="text-green-600 font-medium text-sm">
                      ✓ Force Closed
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add Remarks for Force Closure</h3>
              <button 
                onClick={() => {
                  setShowDialog(false);
                  setSelectedItemId(null);
                  setRemarks("");
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              placeholder="Enter remarks for force closing this item..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="w-full min-h-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setShowDialog(false);
                  setSelectedItemId(null);
                  setRemarks("");
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleItemForceClose}
                disabled={!remarks.trim()}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:bg-red-300 disabled:cursor-not-allowed"
              >
                Force Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SHItemsTable;